/**
 * getEventOrgPerms — selector that returns the calendar permissions object
 * for any org the user has access to.
 *
 * Resolution order:
 *   1. If `orgUnique` matches the current bootstrapped org → use the
 *      already-loaded `accessGroups` (zero extra fetch).
 *   2. Else look up in `eventOrgPerms[orgUnique]` (Phase 3 fan-out).
 *   3. Else return `null` — caller must hide Edit/Delete affordances until
 *      perms are available (or the user reloads).
 *
 * @module getEventOrgPerms
 */
import { buildCalendarPermissionsForOrg } from './buildCalendarPermissionsForOrg.js';
import { pickCalendarBootstrap } from '../../calendar-common/viewStoreKeys.js';

// `buildCalendarPermissionsForOrg` returns a fresh object whose members
// include fresh CLOSURES (`isOwner`, `canEditEvent`, …) — so even a
// shallowEqual `useSelector` sees every call as a new result (react-redux
// unstable-selector warning, tester + owner consoles). The accessGroups
// input is a referentially-stable store value, so cache per input ref.
const permsCache = new WeakMap();

function cachedPermsFor(accessGroups) {
	if (accessGroups == null || typeof accessGroups !== 'object') {
		return accessGroups ? buildCalendarPermissionsForOrg(accessGroups) : null;
	}
	let perms = permsCache.get(accessGroups);
	if (!perms) {
		perms = buildCalendarPermissionsForOrg(accessGroups);
		permsCache.set(accessGroups, perms);
	}
	return perms;
}

export function getEventOrgPerms(state, orgUnique) {
	// View-agnostic: week/day pages register the shared bootstrap slice under
	// their own key (hardcoding the month key disabled canManageMaintenance there).
	const bs = pickCalendarBootstrap(state);
	const currentOrgUnique = bs.organization?.unique || bs.orgUnique || null;

	if (!orgUnique) return null;

	if (orgUnique === currentOrgUnique) {
		const ag = bs.accessGroups;
		return ag ? cachedPermsFor(ag) : null;
	}

	const eventOrgAg = bs.eventOrgPerms?.[orgUnique];
	return eventOrgAg ? cachedPermsFor(eventOrgAg) : null;
}
